import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";
import { Card, CardContent } from "@/components/ui/card";
import { signTextClass } from "@/lib/tone";

import type { Product, WindowTotal } from "../api";
import { formatSignedPct, formatUnits, regionLabel } from "../format";

type Row = Product & { isTotal?: boolean };

/**
 * Own-brand products for the selected window, plus the all-products total AS
 * THE LAST ROW (W8) - a `TOTAL (all products)` row rather than a beside-table
 * block. It is not the sum of the rows above it: the producer ranks and
 * truncates, so the rows are the top N and the total is every product. The
 * row's `title` says so, because a reader who adds up the column and gets a
 * different number needs the explanation right where they are looking.
 */
export function TopProducts({
  products,
  total,
  totalAll,
  periodLabel,
  showTopRegion,
}: {
  products: Product[];
  total: WindowTotal | null;
  totalAll?: WindowTotal | null;
  periodLabel: string;
  showTopRegion: boolean;
}) {
  const columns = showTopRegion ? COLUMNS_WITH_REGION : COLUMNS;
  const displayTotal = totalAll ?? total;
  const rows: Row[] = displayTotal
    ? [
        ...products,
        {
          name: "TOTAL (all products)",
          units: displayTotal.units,
          wow_pct: displayTotal.wow_pct,
          mom_pct: displayTotal.mom_pct,
          yoy_pct: displayTotal.yoy_pct,
          top_region: null,
          isTotal: true,
        },
      ]
    : products;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold text-ks-ink">All products</h2>
          <p className="mt-1 text-xs text-muted-foreground">Own-brand and third-party products by units, {periodLabel}</p>
        </div>

        <DataTable
          columns={columns}
          rows={rows}
          page={{ pageIndex: 0, pageSize: rows.length || 1, rowCount: rows.length }}
          onPageChange={() => {}}
          emptyMessage="No own-brand products sold in this window."
          caption={`Own-brand and third-party products by units, ${periodLabel}`}
          rowClassName={(row) => (row.isTotal ? "font-semibold border-t border-border" : undefined)}
        />
        {totalAll && total && totalAll.units > total.units && <p className="text-xs text-muted-foreground">Includes {formatUnits(totalAll.units - total.units)} third-party units. The Units sold headline above counts own-brand products only.</p>}
      </CardContent>
    </Card>
  );
}

const COLUMNS: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "name",
    header: "Product",
    cell: ({ row }) => (
      <span
        className="text-ks-ink"
        title={
          row.original.isTotal
            ? "Every product in the window, third-party included"
            : undefined
        }
      >
        {row.original.name}
      </span>
    ),
  },
  {
    accessorKey: "units",
    header: "Units",
    cell: ({ row }) => <span className="tabular-nums">{formatUnits(row.original.units)}</span>,
  },
  {
    accessorKey: "wow_pct",
    header: "WoW",
    cell: ({ row }) => (
      <span className={`tabular-nums font-medium ${signTextClass(row.original.wow_pct)}`}>
        {formatSignedPct(row.original.wow_pct)}
      </span>
    ),
  },
  {
    accessorKey: "mom_pct",
    header: "MoM",
    cell: ({ row }) => (
      <span className={`tabular-nums font-medium ${signTextClass(row.original.mom_pct)}`}>
        {formatSignedPct(row.original.mom_pct)}
      </span>
    ),
  },
  {
    accessorKey: "yoy_pct",
    header: "YoY",
    cell: ({ row }) => (
      <span className={`tabular-nums font-medium ${signTextClass(row.original.yoy_pct)}`}>
        {formatSignedPct(row.original.yoy_pct)}
      </span>
    ),
  },
];

/**
 * The overview variant. `top_region` is absent on a region payload - the answer
 * would be the region you are already on - so the column exists only where it
 * says something. The chip LINKS to the region tab (W8): Meteoric's own
 * `/units-sold/<region>` page is gone, but this module's own region tab is the
 * direct replacement, so the link is no longer to a decommissioned surface.
 */
const COLUMNS_WITH_REGION: ColumnDef<Row, unknown>[] = [
  COLUMNS[0]!,
  {
    accessorKey: "top_region",
    header: "Top region",
    cell: ({ row }) => {
      const region = row.original.top_region;
      if (row.original.isTotal || !region) {
        return <span className="text-muted-foreground">n/a</span>;
      }
      // Resolves to /app/customer_acquisition/meteoric_units_sold/$tab - the
      // module's own region tab, replacing Meteoric's decommissioned
      // /units-sold/<region> page. The router registers one generic
      // /app/$area/$module/$tab route rather than a route per module, so the
      // area and module are passed as params rather than baked into `to`.
      return (
        <Link
          to="/app/$area/$module/$tab"
          params={{ area: "customer_acquisition", module: "meteoric_units_sold", tab: "units" }}
          search={{ f_region: region }}
          data-nonmutating
          className="inline-flex rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground hover:text-ks-ink"
        >
          {regionLabel(region)}
        </Link>
      );
    },
  },
  ...COLUMNS.slice(1),
];
